import { Quaternion, Vector3, type Euler, type Object3D } from 'three';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import {
  applyKeyPoseAnkleIkConstraints,
  createEditableKeyPoseScene,
} from '../ik/keyPoseIkSystem';
import {
  applyKeyPoseSnapshotToScene,
  createKeyPoseSnapshotFromScene,
} from '../keyPoseSnapshots';
import type {
  KeyPoseEditingPreviewRecord,
  KeyPoseFkJointId,
  KeyPoseIkTargetPositions,
  KeyPoseQuaternionTuple,
  KeyPoseRecord,
  KeyPoseVector3Tuple,
} from '../types';
import { resolveKeyPoseFkJointObject } from './keyPoseFkConfig';

interface GetKeyPoseFkJointWorldTransformInput {
  scene: Object3D;
  jointId: KeyPoseFkJointId;
}

interface SolveKeyPoseFkRotationInput {
  actorScene: Object3D | null;
  bvh: BVH | null;
  keyPose: KeyPoseRecord | null;
  sceneRotation?: Euler;
  jointId: KeyPoseFkJointId;
  startWorldQuaternion: Quaternion;
  targetWorldQuaternion: Quaternion;
  /** Reused across drag frames to avoid cloning the actor each tick. */
  editableScene?: Object3D | null;
  /**
   * When set with `editableScene`, applies only the delta since the previous
   * gizmo orientation instead of resetting the full skeleton each frame.
   */
  previousTargetWorldQuaternion?: Quaternion | null;
  lockFeet?: boolean;
  pinnedAnklePositions?: KeyPoseIkTargetPositions | null;
}

export interface KeyPoseFkJointWorldTransform {
  position: KeyPoseVector3Tuple;
  quaternion: KeyPoseQuaternionTuple;
}

const MIN_ROTATION_DELTA = 1e-5;

export function getKeyPoseFkJointWorldTransform({
  scene,
  jointId,
}: GetKeyPoseFkJointWorldTransformInput): KeyPoseFkJointWorldTransform | null {
  const object = resolveKeyPoseFkJointObject(scene, jointId);
  if (!object) {
    return null;
  }

  const position = new Vector3();
  const quaternion = new Quaternion();
  object.getWorldPosition(position);
  object.getWorldQuaternion(quaternion);

  return {
    position: [position.x, position.y, position.z],
    quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
  };
}

export function solveKeyPoseFkRotation({
  actorScene,
  bvh,
  keyPose,
  sceneRotation,
  jointId,
  startWorldQuaternion,
  targetWorldQuaternion,
  editableScene,
  previousTargetWorldQuaternion,
  lockFeet = false,
  pinnedAnklePositions = null,
}: SolveKeyPoseFkRotationInput): KeyPoseEditingPreviewRecord | null {
  if (!bvh || !keyPose) {
    return null;
  }

  const scene =
    editableScene ??
    createEditableKeyPoseScene({
      actorScene,
      keyPose,
      sceneRotation,
    });
  if (!scene) {
    return null;
  }

  const useIncrementalDragStep =
    Boolean(editableScene) && Boolean(previousTargetWorldQuaternion);

  if (editableScene && !useIncrementalDragStep) {
    applyKeyPoseSnapshotToScene(scene, keyPose.skeletonSnapshot);
    scene.updateMatrixWorld(true);
  }

  const joint = resolveKeyPoseFkJointObject(scene, jointId);
  if (!joint) {
    return null;
  }

  const deltaWorldQuaternion = useIncrementalDragStep
    ? targetWorldQuaternion
        .clone()
        .multiply(previousTargetWorldQuaternion!.clone().invert())
        .normalize()
    : targetWorldQuaternion
        .clone()
        .multiply(startWorldQuaternion.clone().invert())
        .normalize();

  if (isTinyRotation(deltaWorldQuaternion)) {
    return null;
  }

  applyWorldRotationDelta(joint, deltaWorldQuaternion);
  scene.updateMatrixWorld(true);

  if (lockFeet && pinnedAnklePositions) {
    applyKeyPoseAnkleIkConstraints(scene, pinnedAnklePositions);
  }

  const snapshot = createKeyPoseSnapshotFromScene({
    scene,
    bvh,
    frame: keyPose.frame,
  });
  if (!snapshot) {
    return null;
  }

  return {
    skeletonSnapshot: snapshot.skeletonSnapshot,
    sourcePose: snapshot.sourcePose,
  };
}

function applyWorldRotationDelta(
  object: Object3D,
  deltaWorldQuaternion: Quaternion,
) {
  const parent = object.parent;
  if (!parent) {
    object.quaternion.premultiply(deltaWorldQuaternion).normalize();
    object.updateMatrixWorld(true);
    return;
  }

  const parentWorldQuaternion = new Quaternion();
  parent.getWorldQuaternion(parentWorldQuaternion);
  const parentWorldQuaternionInverse = parentWorldQuaternion.clone().invert();
  const localDeltaQuaternion = parentWorldQuaternionInverse
    .multiply(deltaWorldQuaternion)
    .multiply(parentWorldQuaternion);

  object.quaternion.premultiply(localDeltaQuaternion).normalize();
  object.updateMatrixWorld(true);
}

function isTinyRotation(quaternion: Quaternion) {
  return (
    Math.abs(quaternion.x) +
      Math.abs(quaternion.y) +
      Math.abs(quaternion.z) <
    MIN_ROTATION_DELTA
  );
}
